import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import Head from '@/components/base/Head';
import { getStore } from '@/lib/store';

interface TeamMember {
  id: string;
  name: string;
  title: string;
  specialty: string;
  bio: string;
  certifications: string[];
  languages: string[];
  imageUrl: string;
}

function getTeamMembers(): TeamMember[] {
  return getStore().team
    .filter((m) => m.visible)
    .map((m) => ({
      id: m.id,
      name: m.name,
      title: m.title ?? '',
      specialty: m.specialty ?? '',
      bio: m.bio ?? '',
      certifications: m.certifications ?? [],
      languages: m.languages ?? [],
      imageUrl: m.image_url ?? '',
    }));
}

export default function TeamPage() {
  const { t } = useTranslation();
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  const [members, setMembers] = useState<TeamMember[]>(getTeamMembers());

  useEffect(() => {
    const handleUpdate = () => {
      setMembers(getTeamMembers());
    };
    handleUpdate();
    window.addEventListener('admin-content-updated', handleUpdate);
    window.addEventListener('admin-data-updated', handleUpdate);
    return () => {
      window.removeEventListener('admin-content-updated', handleUpdate);
      window.removeEventListener('admin-data-updated', handleUpdate);
    };
  }, []);

  const languageFlags: Record<string, string> = {
    DE: '🇩🇪',
    IT: '🇮🇹',
    EN: '🇬🇧',
    FR: '🇫🇷',
    ES: '🇪🇸',
    NL: '🇳🇱',
    PL: '🇵🇱',
    AL: '🇦🇱',
    SQ: '🇦🇱',
  };

  return (
    <>
      <Head
        title={t('seo.team.title')}
        metas={[
          { name: 'description', content: t('seo.team.description') },
          { name: 'keywords', content: t('seo.team.keywords') },
          { property: 'og:title', content: t('seo.team.title') },
          { property: 'og:description', content: t('seo.team.description') },
          { property: 'og:url', content: 'https://estevaclinic.com/team' },
          { property: 'og:type', content: 'website' },
          { property: 'og:locale', content: t('seo.ogLocale') },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: t('seo.team.title') },
          { name: 'twitter:description', content: t('seo.team.description') },
          { name: 'last-modified', content: '2026-06-10' },
        ]}
        links={[
          { rel: 'canonical', href: 'https://estevaclinic.com/team' },
        ]}
      />
      <main className="bg-background-50 min-h-screen">
      <Navbar />

      <section className="relative pt-24 md:pt-32 pb-10 md:pb-14 px-6 md:px-10 bg-foreground-950 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-950/30 via-foreground-950 to-accent-950/20"></div>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-primary-500/5 blur-[120px]"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-accent-500/5 blur-[100px]"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white/70 text-xs tracking-[0.2em] uppercase font-label">
            {t('team.tag')}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mt-4 leading-tight">
            {t('team.heading_line1')}<br />
            <span className="italic font-light">{t('team.heading_line2')}</span>
          </h1>
          <p className="mt-4 text-sm md:text-base text-white/55 max-w-2xl mx-auto font-body leading-relaxed">
            {t('team.subtitle')}
          </p>
        </div>
      </section>

      <section className="py-12 md:py-20 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {members.map((member) => (
              <div
                key={member.id}
                onClick={() => setSelectedMember(selectedMember === member.id ? null : member.id)}
                className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:border-primary-200/50 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-48 sm:h-56 md:h-72 overflow-hidden">
                  <img
                    src={member.imageUrl}
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://readdy.ai/api/search-image?query=Professional%20medical%20doctor%20portrait%20neutral%20background%20warm%20lighting%20clean%20aesthetic%20editorial%20photography&width=400&height=500&seq=team-fallback&orientation=portrait';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900">
                    {member.name}
                  </h3>
                  <p className="text-xs tracking-[0.1em] uppercase font-label font-semibold text-primary-600 mt-1">
                    {t(`team.${member.id}.title`, { defaultValue: member.title })}
                  </p>
                  <p className="text-sm text-foreground-500 font-body mt-2">
                    {t(`team.${member.id}.specialty`, { defaultValue: member.specialty })}
                  </p>
                  <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                    {member.languages.map((lang) => (
                      <span
                        key={lang}
                        className="text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 rounded-full bg-background-100 border border-background-200 text-foreground-500 font-label whitespace-nowrap"
                      >
                        {languageFlags[lang] || '🏳️'} {lang}
                      </span>
                    ))}
                  </div>
                  <div className={`mt-4 overflow-hidden transition-all duration-400 ${selectedMember === member.id ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="pt-3 border-t border-background-200">
                      <p className="text-sm text-foreground-600 font-body leading-relaxed">
                        {t(`team.${member.id}.bio`, { defaultValue: member.bio })}
                      </p>
                      <div className="mt-4 space-y-1.5">
                        {(() => {
                          const certs = t(`team.${member.id}.certifications`, { returnObjects: true }) as string[] | undefined;
                          const certifications = Array.isArray(certs) ? certs : member.certifications;
                          return certifications.map((cert, idx) => (
                            <div key={`${member.id}-cert-${idx}`} className="flex items-center gap-2 text-xs text-foreground-500 font-body">
                              <div className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                                <i className="ri-verified-badge-line text-accent-600 text-xs"></i>
                              </div>
                              {cert}
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>
                  <button
                    className="mt-3 text-xs font-label font-semibold text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  >
                    {selectedMember === member.id ? t('team.show_less') : t('team.show_more')}
                    <i className={`text-xs transition-transform duration-300 ${selectedMember === member.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 px-6 md:px-10 bg-primary-950 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-primary-500/5 blur-[150px]"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-accent-500/5 blur-[120px]"></div>
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <h2 className="font-heading text-xl md:text-4xl font-bold text-white leading-tight">
            {t('team.cta_heading')}
          </h2>
          <p className="mt-3 md:mt-4 text-sm md:text-base text-white/55 max-w-xl mx-auto font-body leading-relaxed px-2">
            {t('team.cta_subtitle')}
          </p>
          <a
            href="/booking"
            className="inline-flex items-center gap-2 mt-6 md:mt-7 px-6 md:px-10 py-3 md:py-4 bg-white text-primary-800 rounded-full text-xs md:text-sm tracking-[0.12em] uppercase font-label font-semibold hover:bg-white/95 hover:scale-[1.02] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            {t('team.cta_button')}
            <i className="ri-arrow-right-line text-base"></i>
          </a>
        </div>
      </section>

      <Footer />
    </main>
    </>
  );
}